import { ApeironCloseButton, useChainSwitcher } from '@apeiron/temp'
import { TabContext, TabPanel } from '@mui/lab'
import {
  Box,
  Dialog,
  Paper,
  styled,
  Tab,
  Tabs,
  Typography
} from '@mui/material'
import AuctionView from '@src/components/share/trade/sellDialogView/TradeSellAuctionView'
import FixedPriceView from '@src/components/share/trade/sellDialogView/TradeSellFixedPriceView'
import { TRADE_PART_MAP } from '@src/constants/trade'
import { SaleMethod, TradeItem, TradeParty } from '@src/types/trade'
import * as R from 'ramda'
import { FC, SyntheticEvent, useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

enum SellDialogTab {
  Fixed = 0,
  Auction
}

const DialogPaper = styled(Paper)`
  background-color: #262829;
  border-radius: 5px;
  color: #ffffff;
  max-width: 600px;
  width: 100%;
`

const DialogTitle = styled(Box)`
  font-size: 26px;
  line-height: 30px;
  ${props => props.theme.breakpoints.up('res480')} {
    margin: 24px 0 6px 42px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    margin: 44px 20px 6px 20px;
  }
`

const DialogDescription = styled(Box)`
  color: #838383;
  font-size: 16px;
  line-height: 30px;
  ${props => props.theme.breakpoints.up('res480')} {
    margin: 0px 42px 8px 42px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    margin: 0px 22px 8px 22px;
  }
`

const StyledTabs = styled(Tabs)`
  .MuiTabs-flexContainer {
    gap: 60px;
  }
`

const StyledTab = styled(Tab)`
  height: 64px;
  border-radius: 6px;
  &[aria-selected='true'] {
    background-color: #171719;
  }
  ${props => props.theme.breakpoints.up('res480')} {
    width: 200px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    width: 33%;
  }
`

const TabText = styled(Typography)`
  font-size: 20px;
  line-height: 24px;
  color: #ffffff;
  text-transform: none;
`

const StyledTabPanel = styled(TabPanel)`
  position: relative;
  margin-top: -7px;
  background-color: #171719;
  z-index: 5;
  ${props => props.theme.breakpoints.up('res480')} {
    padding: 22px 20px 40px 20px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    padding: 22px 10px 40px 10px;
  }
`

const HighlightDescriptionText = styled('span')`
  color: #ffffff;
  font-size: 16px;
  line-height: 30px;
`

const StyledCloseButton = styled(ApeironCloseButton)`
  position: absolute;

  ${props => props.theme.breakpoints.up('res480')} {
    top: 20px;
    right: 20px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    top: 12px;
    right: 12px;
  }
`

const tabValueAuction = SellDialogTab.Auction.toString()

const tabValueFixed = SellDialogTab.Fixed.toString()

const TradeSellDialog: FC<Props> = (props: Props) => {
  const {
    checkboxRemark,
    className,
    description,
    isEditMode = false,
    isOpened,
    item,
    loading,
    title,
    totalQuantity = -1,
    onConfirm,
    onClose
  } = props

  const { t } = useTranslation()

  const { getChainType, detectWalletType } = useChainSwitcher()

  const chainType = getChainType(detectWalletType())

  const tradeParties: TradeParty[] = TRADE_PART_MAP[chainType] || []

  const defaultTab =
    isEditMode && R.pathOr(0, ['listingInfo', 'duration'], item) > 0
      ? tabValueAuction
      : tabValueFixed

  const [tabIndex, setTabIndex] = useState<string>(defaultTab)

  const listingInfo = isEditMode ? item.listingInfo : null

  const handleOnSwitchTab = useCallback((_: SyntheticEvent, value: string) => {
    setTabIndex(value)
  }, [])

  const handleOnConfirm = useCallback(
    (saleMethod: SaleMethod, quantity: number) => {
      if (onConfirm) {
        onConfirm(isEditMode, item, saleMethod, quantity)
      }
    },
    [isEditMode, item, onConfirm]
  )

  const onConditionalClose = loading ? () => {} : onClose

  return (
    <Dialog
      className={className}
      open={isOpened}
      onClose={onConditionalClose}
      PaperComponent={DialogPaper}
    >
      <StyledCloseButton onClick={onConditionalClose} />
      <DialogTitle>{title}</DialogTitle>
      <DialogDescription>
        <Trans components={{ highlight: <HighlightDescriptionText /> }}>
          {description}
        </Trans>
      </DialogDescription>
      <TabContext value={tabIndex}>
        <StyledTabs centered value={tabIndex} onChange={handleOnSwitchTab}>
          <StyledTab
            label={<TabText>{t('dialog.sell.fixed_price.title')}</TabText>}
            value={tabValueFixed}
          />
          <StyledTab
            label={<TabText>{t('dialog.sell.auction.title')}</TabText>}
            value={tabValueAuction}
          />
        </StyledTabs>
        <StyledTabPanel value={tabValueFixed}>
          <FixedPriceView
            checkboxRemark={checkboxRemark}
            isEditMode={isEditMode}
            listingInfo={listingInfo}
            loading={loading}
            totalQuantity={totalQuantity}
            tradeParties={tradeParties}
            onConfirm={handleOnConfirm}
          />
        </StyledTabPanel>
        <StyledTabPanel value={tabValueAuction}>
          <AuctionView
            checkboxRemark={checkboxRemark}
            isEditMode={isEditMode}
            listingInfo={listingInfo}
            loading={loading}
            totalQuantity={totalQuantity}
            tradeParties={tradeParties}
            onConfirm={handleOnConfirm}
          />
        </StyledTabPanel>
      </TabContext>
    </Dialog>
  )
}

type Props = {
  checkboxRemark?: string
  className?: string
  description: string
  isEditMode?: boolean
  isOpened: boolean
  item: TradeItem
  loading: boolean
  title: string
  totalQuantity?: number
  onClose?: () => void
  onConfirm?: (
    update: boolean,
    item: TradeItem,
    saleMethod: SaleMethod,
    quantity: number
  ) => void
}

export default TradeSellDialog
